// frontend/src/pages/AdminConsole.jsx
import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { Shield, Copy, Check, ChevronUp, ChevronDown, Users, Mail, DollarSign, AlertTriangle, X } from 'lucide-react';
import CostsTab from '../components/admin/CostsTab';

const SEGMENT_STYLES = {
  trial: 'bg-blue-100 text-blue-700',
  paying: 'bg-green-100 text-green-700',
  churned: 'bg-red-100 text-red-700',
  free: 'bg-gray-100 text-gray-600',
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  canceled: 'bg-red-100 text-red-700',
  unpaid: 'bg-red-100 text-red-700',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

function CopyableEmail({ email }) {
  const [copied, setCopied] = useState(false);
  const timerRef = React.useRef(null);

  // Clean up timeout on unmount
  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Fallback for insecure contexts
      const el = document.createElement('textarea');
      el.value = email;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="group flex items-center gap-1.5 min-h-[44px] sm:min-h-0 text-left hover:text-[#2d7a5f] transition-colors"
      title="Click to copy"
    >
      <span className="truncate max-w-[200px]">{email}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      )}
    </button>
  );
}

export default function AdminConsole() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('users');
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState(null);
  const [budgetAlerts, setBudgetAlerts] = useState(null);
  const [budgetAlertDismissed, setBudgetAlertDismissed] = useState(false);
  const waitlistFetched = React.useRef(false);

  // Fetch budget alerts on mount
  useEffect(() => {
    api.get('/api/admin/costs/summary')
      .then(res => {
        const budgets = res.data.budgets || [];
        const alerts = budgets.filter(b => b.pct >= 80);
        if (alerts.length > 0) setBudgetAlerts(alerts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchUsers() {
      try {
        const res = await api.get('/api/admin/users');
        if (!cancelled) setUsers(res.data.users);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  // Lazy-fetch waitlist on first tab visit
  useEffect(() => {
    if (activeTab !== 'waitlist' || waitlistFetched.current) return;
    let cancelled = false;
    async function fetchWaitlist() {
      setWaitlistLoading(true);
      setWaitlistError(null);
      try {
        const res = await api.get('/api/admin/waitlist');
        if (!cancelled) {
          setWaitlist(res.data.waitlist);
          waitlistFetched.current = true;
        }
      } catch (err) {
        if (!cancelled) setWaitlistError(err.response?.data?.error || 'Failed to load waitlist');
      } finally {
        if (!cancelled) setWaitlistLoading(false);
      }
    }
    fetchWaitlist();
    return () => { cancelled = true; };
  }, [activeTab]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' || field === 'last_login_at' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle nulls
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric
      if (sortField === 'account_count') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Dates
      if (sortField === 'created_at' || sortField === 'last_login_at') {
        return sortDir === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      // Strings
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortField, sortDir]);

  // Summary stats
  const stats = useMemo(() => {
    const segments = { trial: 0, paying: 0, churned: 0, free: 0 };
    users.forEach(u => { segments[u.segment] = (segments[u.segment] || 0) + 1; });
    return { total: users.length, ...segments };
  }, [users]);

  const SortHeader = ({ field, label, className = '' }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[#2d7a5f]/10 rounded-lg">
          <Shield className="w-6 h-6 text-[#2d7a5f]" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold text-[#2c2825]">Admin Console</h1>
          <p className="text-sm text-gray-500">{stats.total} registered user{stats.total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Budget Alert Banner */}
      {budgetAlerts && !budgetAlertDismissed && (() => {
        const hasOver = budgetAlerts.some(b => b.pct >= 100);
        const bannerStyles = hasOver
          ? 'bg-red-50 border-red-200 text-red-700'
          : 'bg-amber-50 border-amber-200 text-amber-800';
        return (
          <div className={`rounded-xl border p-3 mb-4 flex items-start gap-2 ${bannerStyles}`}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <span className="font-medium">Budget alert: </span>
              {budgetAlerts.map((b, i) => (
                <span key={b.vendor}>
                  {i > 0 && ', '}
                  <span className="capitalize">{b.vendor}</span> at {b.pct}%
                </span>
              ))}
            </div>
            <button
              onClick={() => setBudgetAlertDismissed(true)}
              className="flex-shrink-0 p-0.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded hover:bg-black/5 transition-colors flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6" aria-label="Admin tabs">
          {[
            { key: 'users', label: 'Users', count: stats.total, icon: Users },
            { key: 'waitlist', label: 'Waitlist', count: waitlist.length, icon: Mail },
            { key: 'costs', label: 'Costs', count: null, icon: DollarSign },
          ].map(({ key, label, count, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-0 whitespace-nowrap pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'text-[#2d7a5f] border-[#2d7a5f]'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}{count != null ? ` (${count})` : ''}
            </button>
          ))}
        </nav>
      </div>

      {/* ─── Users Tab ─── */}
      {activeTab === 'users' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'bg-gray-50 border-gray-200' },
              { label: 'Trial', value: stats.trial, color: 'bg-blue-50 border-blue-200' },
              { label: 'Paying', value: stats.paying, color: 'bg-green-50 border-green-200' },
              { label: 'Churned', value: stats.churned, color: 'bg-red-50 border-red-200' },
            ].map(({ label, value, color }) => (
              <div key={label} className={`rounded-xl border p-4 ${color}`}>
                <div className="text-2xl font-bold text-[#2c2825]">{value}</div>
                <div className="text-xs text-gray-500 font-medium mt-1">{label}</div>
              </div>
            ))}
          </div>

          {/* Users Table — Desktop */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortHeader field="first_name" label="Name" />
                    <SortHeader field="email" label="Email" />
                    <SortHeader field="created_at" label="Signed Up" />
                    <SortHeader field="last_login_at" label="Last Login" />
                    <SortHeader field="onboarding_step" label="Onboarding" />
                    <SortHeader field="account_count" label="Accounts" />
                    <SortHeader field="subscription_status" label="Status" />
                    <SortHeader field="segment" label="Segment" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-[#2c2825] whitespace-nowrap">
                        {user.first_name || user.last_name ? (
                          <>
                            {user.first_name && <span className="font-semibold">{user.first_name}</span>}
                            {user.first_name && user.last_name && ' '}
                            {user.last_name && <span className="font-normal text-gray-500">{user.last_name}</span>}
                          </>
                        ) : '--'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <CopyableEmail email={user.email} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {user.last_login_at ? formatRelative(user.last_login_at) : (
                          <span className="text-gray-400 italic" title={`Signed up ${formatDate(user.created_at)}`}>
                            Since {formatDate(user.created_at)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          user.onboarding_step === 'complete'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {user.onboarding_step || '--'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">
                        {user.account_count}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {user.subscription_status ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            STATUS_STYLES[user.subscription_status] || 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.subscription_status}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">none</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          SEGMENT_STYLES[user.segment] || 'bg-gray-100 text-gray-600'
                        }`}>
                          {user.segment}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Users Cards — Mobile/Tablet */}
          <div className="lg:hidden space-y-3">
            {sorted.map(user => (
              <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[#2c2825]">
                      {user.first_name || user.last_name ? (
                        <>
                          {user.first_name && <span className="font-semibold">{user.first_name}</span>}
                          {user.first_name && user.last_name && ' '}
                          {user.last_name && <span className="font-normal text-gray-500">{user.last_name}</span>}
                        </>
                      ) : '--'}
                    </div>
                    <CopyableEmail email={user.email} />
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    SEGMENT_STYLES[user.segment] || 'bg-gray-100 text-gray-600'
                  }`}>
                    {user.segment}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-400 text-xs">Signed Up</span>
                    <div className="text-gray-600">{formatDate(user.created_at)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Last Login</span>
                    <div className="text-gray-600">
                      {user.last_login_at ? formatRelative(user.last_login_at) : (
                        <span className="text-gray-400 italic">Since {formatDate(user.created_at)}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Onboarding</span>
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        user.onboarding_step === 'complete'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {user.onboarding_step || '--'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Accounts</span>
                    <div className="text-gray-600">{user.account_count}</div>
                  </div>
                  {user.subscription_status && (
                    <div>
                      <span className="text-gray-400 text-xs">Status</span>
                      <div>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          STATUS_STYLES[user.subscription_status] || 'bg-gray-100 text-gray-600'
                        }`}>
                          {user.subscription_status}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ─── Waitlist Tab ─── */}
      {activeTab === 'waitlist' && (
        <>
          {/* Summary Card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <div className="rounded-xl border p-4 bg-purple-50 border-purple-200">
              <div className="text-2xl font-bold text-[#2c2825]">{waitlist.length}</div>
              <div className="text-xs text-gray-500 font-medium mt-1">Waitlist Signups</div>
            </div>
          </div>

          {waitlistLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-64 bg-gray-100 rounded-xl" />
            </div>
          ) : waitlistError ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
              {waitlistError}
            </div>
          ) : waitlist.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No waitlist signups yet</p>
            </div>
          ) : (
            <>
              {/* Waitlist Table — Desktop */}
              <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Signed Up</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {waitlist.map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <CopyableEmail email={entry.email} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatDate(entry.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Waitlist Cards — Mobile/Tablet */}
              <div className="lg:hidden space-y-3">
                {waitlist.map(entry => (
                  <div key={entry.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                    <CopyableEmail email={entry.email} />
                    <span className="text-sm text-gray-500 whitespace-nowrap ml-4">{formatDate(entry.created_at)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ─── Costs Tab ─── */}
      {activeTab === 'costs' && <CostsTab />}
    </div>
  );
}
